import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Scan, Camera, Upload, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BarcodeScanner = ({ token }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState('manual');
  const [barcode, setBarcode] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const webcamRef = useRef(null);

  const scanBarcode = async (barcodeValue) => {
    if (!barcodeValue) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/food/scan-barcode`,
        { barcode: barcodeValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data.product);
    } catch (error) {
      console.error('Error scanning barcode:', error);
      alert(error.response?.data?.detail || 'Failed to scan barcode');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleManualScan = () => {
    scanBarcode(barcode);
  };

  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
    // In a real implementation, you would use a barcode detection library here
    alert('Barcode detection from camera is a premium feature. For now, please enter the barcode manually.');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedImage(reader.result);
        // In a real implementation, you would use a barcode detection library here
        alert('Barcode detection from images is a premium feature. For now, please enter the barcode manually.');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleManualScan();
    }
  };

  return (
    <div className="space-y-4" data-testid="barcode-scanner">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
        <h2 className="text-lg font-bold mb-2 flex items-center gap-2">
          <Scan size={20} className="text-[#FF6600]" />
          Barcode Scanner
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Scan product barcodes to get instant nutritional information
        </p>
      </div>

      {/* Mode Selection */}
      <div className="flex gap-2">
        <button
          onClick={() => setMode('manual')}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 ${mode === 'manual' ? 'bg-[#FF6600] text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'}`}
        >
          <Search size={16} />
          Manual Entry
        </button>
        <button
          onClick={() => setMode('camera')}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 ${mode === 'camera' ? 'bg-[#FF6600] text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'}`}
        >
          <Camera size={16} />
          Camera
        </button>
        <button
          onClick={() => setMode('upload')}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 ${mode === 'upload' ? 'bg-[#FF6600] text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'}`}
        >
          <Upload size={16} />
          Upload
        </button>
      </div>

      {/* Manual Entry */}
      {mode === 'manual' && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          <h3 className="font-bold mb-4">Enter Barcode Number</h3>
          <div className="flex gap-3">
            <input
              type="text"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter barcode (e.g., 012345678901)"
              className="flex-1 p-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
            />
            <button
              onClick={handleManualScan}
              disabled={loading || !barcode}
              className="px-4 py-2 bg-[#FF6600] text-white rounded-lg font-semibold hover:bg-[#E55C00] disabled:opacity-50"
            >
              Scan
            </button>
          </div>
        </div>
      )}

      {/* Camera Mode */}
      {mode === 'camera' && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          <h3 className="font-bold mb-4">Scan with Camera</h3>
          {!capturedImage ? (
            <div>
              <Webcam
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="w-full rounded-lg mb-4"
                videoConstraints={{ facingMode: 'environment' }}
              />
              <button onClick={capturePhoto} className="w-full py-3 bg-[#FF6600] text-white rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-[#E55C00]">
                <Camera size={18} />
                Capture Barcode
              </button>
            </div>
          ) : (
            <div>
              <img src={capturedImage} alt="Captured" className="w-full rounded-lg mb-4" />
              <button
                onClick={() => setCapturedImage(null)}
                className="w-full py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:bg-gray-900"
              >
                Retake
              </button>
            </div>
          )}
        </div>
      )}

      {/* Upload Mode */}
      {mode === 'upload' && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          <h3 className="font-bold mb-4">Upload Barcode Image</h3>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              id="barcode-upload"
            />
            <label htmlFor="barcode-upload" className="cursor-pointer">
              <Upload size={36} className="mx-auto mb-3 text-gray-400" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Click to upload barcode image</p>
            </label>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm text-center">
          <div className="text-sm text-gray-500 dark:text-gray-400">Scanning barcode...</div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border-2 border-[#FF6600]">
          <h3 className="text-lg font-bold mb-4">Product Found!</h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-bold mb-1">{result.name}</h4>
              {result.brand && (
                <p className="text-xs text-gray-500 dark:text-gray-400">{result.brand}</p>
              )}
            </div>

            <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
              <div className="text-center">
                <div className="font-bold text-[#FF6600]">{Math.round(result.calories)}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Calories</div>
              </div>
              <div className="text-center">
                <div className="font-bold">{Math.round(result.protein || 0)}g</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Protein</div>
              </div>
              <div className="text-center">
                <div className="font-bold">{Math.round(result.carbs || 0)}g</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Carbs</div>
              </div>
              <div className="text-center">
                <div className="font-bold">{Math.round(result.fats || 0)}g</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Fats</div>
              </div>
            </div>

            {result.serving_size && (
              <div className="text-sm">
                <span className="text-gray-500 dark:text-gray-400">Serving size:</span> {result.serving_size}
              </div>
            )}

            {result.ingredients && (
              <div>
                <h5 className="font-semibold text-sm mb-2">Ingredients:</h5>
                <p className="text-xs text-gray-600 dark:text-gray-300">{result.ingredients}</p>
              </div>
            )}

            <button
              onClick={() => alert('Quick add feature coming soon!')}
              className="w-full py-3 bg-[#FF6600] text-white rounded-lg font-semibold hover:bg-[#E55C00]"
            >
              Add to My Meals
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BarcodeScanner;
